/**
 * Resolves the DOM node overlays should portal into.
 *
 * Normally `document.body`. While an element is fullscreen, the browser paints
 * ONLY that element's subtree — anything portalled to `body` is still in the
 * document, still "visible" to tests, and simply never drawn. So every overlay
 * that portals (modals, the selection bubble menu, toasts, the undo snackbar)
 * has to follow the fullscreen element or it silently disappears the moment the
 * editor goes full screen.
 *
 * The hook subscribes to `fullscreenchange` so consumers re-render and React
 * moves the portal, rather than reading the container once at mount and being
 * wrong for the rest of the session.
 */

import { useEffect, useState } from 'react';

/** The current fullscreen element, or `document.body` when there is none. */
function currentContainer(): HTMLElement {
  const fullscreen = document.fullscreenElement;
  return fullscreen instanceof HTMLElement ? fullscreen : document.body;
}

/**
 * Returns the node overlays should portal into, tracking fullscreen changes.
 *
 * @returns The active fullscreen element, or `document.body` when nothing is
 *   fullscreen.
 */
export function usePortalContainer(): HTMLElement {
  const [container, setContainer] = useState<HTMLElement>(currentContainer);

  useEffect(() => {
    const sync = (): void => setContainer(currentContainer());
    // Re-read on mount too: a component mounted WHILE fullscreen is already
    // active never sees a change event.
    sync();
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  return container;
}
